// Module 4 / Slice 5a — Workforce employee list screen.
//
// Read-first paginated list of workforce.employee rows; filters compose
// status (multi-select) + free-text query against employeeNumber /
// forename / surname. Each row links to the detail screen via the
// TanStack Router /admin/workforce/employees/$employeeId route.
//
// Per MODULE_4_PLAN.md §10 the SPA stays behind the
// `workforce.admin_ui.enabled` feature flag — wiring happens at the
// AppShell nav level (route remains reachable for power users).
//
// PATTERNS.md §2.2 (screen composition) + §2.5 (data-testid naming).

import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { workforceApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";

type EmploymentStatus = "active" | "inactive" | "retired" | "on_leave";

const STATUS_OPTIONS: readonly EmploymentStatus[] = [
  "active",
  "inactive",
  "retired",
  "on_leave",
];

export function EmployeeListScreen(): JSX.Element {
  const { t } = useI18n();
  const [q, setQ] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<EmploymentStatus | "">("");

  const query = useQuery({
    queryKey: ["workforce", "employees", { q, status: statusFilter }] as const,
    queryFn: async () => {
      const response = await workforceApiClient.GET("/workforce/employees", {
        params: {
          query: {
            limit: 200,
            includeArchived: false,
            ...(q.trim().length > 0 ? { q: q.trim() } : {}),
            ...(statusFilter !== "" ? { status: [statusFilter] } : {}),
          },
        },
      });
      if (!response.data) {
        throw new Error("workforce_employees_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div>
      <h1>{t("workforce.employees.heading")}</h1>
      <p className="rc-help">{t("workforce.employees.description")}</p>

      <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <label htmlFor="workforce-employee-q">
          {t("workforce.employees.filter.q")}{" "}
          <input
            id="workforce-employee-q"
            data-testid="employee-filter-q"
            type="search"
            value={q}
            placeholder={t("workforce.employees.filter.qPlaceholder")}
            onChange={(e) => setQ(e.target.value)}
            style={{ marginLeft: "0.5rem" }}
          />
        </label>

        <label htmlFor="workforce-employee-status">
          {t("workforce.employees.filter.status")}{" "}
          <select
            id="workforce-employee-status"
            data-testid="employee-filter-status"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter((e.target.value as EmploymentStatus | "") ?? "")
            }
            style={{ marginLeft: "0.5rem" }}
          >
            <option value="">{t("workforce.employees.filter.status.all")}</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`workforce.employees.filter.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length === 0 && (
        <p data-testid="employee-empty">{t("workforce.employees.empty")}</p>
      )}
      {query.data && query.data.items && query.data.items.length > 0 && (
        <table className="rc-table" data-testid="employee-table">
          <thead>
            <tr>
              <th>{t("workforce.employees.column.employeeNumber")}</th>
              <th>{t("workforce.employees.column.displayName")}</th>
              <th>{t("workforce.employees.column.status")}</th>
              <th>{t("workforce.employees.column.hireDate")}</th>
              <th>{t("workforce.employees.column.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((emp) => (
              <tr key={emp.id} data-testid={`employee-row-${emp.id}`}>
                <td>
                  <code>{emp.employeeNumber}</code>
                </td>
                <td>{emp.displayName}</td>
                <td>
                  <span data-testid={`employee-status-${emp.id}`}>
                    {emp.employmentStatus}
                  </span>
                </td>
                <td>{emp.hireDate ?? "\u2014"}</td>
                <td>
                  <Link
                    to="/admin/workforce/employees/$employeeId"
                    params={{ employeeId: emp.id }}
                    data-testid={`employee-view-${emp.id}`}
                  >
                    {t("workforce.employees.action.view")}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
